import { useState } from "react";
import { motion } from "framer-motion";
import { X, Eye, Images } from "@phosphor-icons/react";
import AnimatedSection from "@/components/layout/AnimatedSection";
import { galleryItems, fadeUp, WHATSAPP_QUOTE_URL } from "@/data/site";

const Gallery = ({ showHeading = true }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <AnimatedSection id="galeria" className="section-padding bg-[#F5F5F5]">
      <div className="container-custom">
        {showHeading && (
          <motion.div variants={fadeUp} className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E73336] mb-4 block">
              Nosso Portfólio
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#234D89] mb-4">
              Galeria de Trabalhos
            </h2>
            <div className="section-divider mx-auto mb-6" />
            <p className="text-[#5C5C5C] max-w-2xl mx-auto">
              Conheça alguns dos nossos trabalhos de acabamento gráfico. Qualidade que você pode ver em cada detalhe.
            </p>
          </motion.div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryItems.map((item, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              custom={index}
              data-testid={`gallery-item-${index}`}
              className="group relative overflow-hidden rounded-2xl cursor-pointer"
              onClick={() => setSelectedImage(item)}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#234D89]/90 via-[#234D89]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                <span className="text-xs font-bold uppercase tracking-wider text-[#E73336] mb-1">
                  {item.category}
                </span>
                <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                <div className="mt-3 flex items-center gap-2 text-white/80 text-sm">
                  <Eye size={18} />
                  <span>Ver detalhes</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div variants={fadeUp} className="text-center mt-12">
          <a
            href={WHATSAPP_QUOTE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-cta-button"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#E73336] text-white font-bold text-sm uppercase tracking-wide rounded-full hover:bg-[#BD2A2C] transition-all btn-cta"
          >
            <Images size={20} />
            Quero um Acabamento Assim
          </a>
        </motion.div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="gallery-lightbox"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-[#E73336] transition-colors"
              data-testid="lightbox-close-button"
              aria-label="Fechar"
            >
              <X size={32} />
            </button>

            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="w-full h-auto rounded-2xl"
            />

            <div className="mt-4 text-center">
              <span className="text-[#E73336] text-sm font-bold uppercase tracking-wider">
                {selectedImage.category}
              </span>
              <h3 className="text-white text-2xl font-semibold mt-1">
                {selectedImage.title}
              </h3>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatedSection>
  );
};

export default Gallery;
